import React, { useState } from "react";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  FormHelperText,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { FormikProvider, useFormik } from "formik";
import { useRegisterUserMutation } from "../../generated/graphql";
import { useAppDispatch } from "../../hooks/usereduxHooks";
import { loginAction } from "../../store/actionCreator/auth.creator";
import { sweetAlert } from "../../utils/sweetAlert";
import { SignupValidation } from "../../validation/signupFeildValidation";

export default function UserNameInput() {
  const dispatch = useAppDispatch();
  const [showPassword, setShowPassword] = useState(false);

  const [signUpRequest] = useRegisterUserMutation({
    onCompleted: (data) => {
      const { email, jwtToken, name, id, timestamps } = data.registerUser;
      dispatch(
        loginAction({ email, jwtToken, name, id, password: "", timestamps }),
      );
    },
    onError: (err) => {
      sweetAlert(err.message, "error");
    },
  });

  const formik = useFormik({
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        await signUpRequest({
          variables: {
            RegisterUserInput: {
              ...values,
            },
          },
        });
      } catch (err) {}
    },
    initialValues: {
      email: "",
      name: "",
      password: "",
    },
    validationSchema: SignupValidation,
  });

  const {
    handleSubmit,
    handleBlur,
    errors,
    handleChange,
    touched,
    values,
    isSubmitting,
  } = formik;

  return (
    <FormikProvider value={formik}>
      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 2, width: "100%" }}>
        <Box sx={{ mb: 2 }}>
          <TextField
            margin="none"
            required
            fullWidth
            label="Name"
            placeholder="Full Name"
            id="name"
            value={values.name}
            name="name"
            onChange={handleChange}
            onBlur={handleBlur}
            autoComplete="name"
            autoFocus
            error={Boolean(touched.name && errors.name)}
          />
          {touched.name && errors.name && (
            <FormHelperText error sx={{ mt: 0.5, mx: 1 }}>
              {errors.name}
            </FormHelperText>
          )}
        </Box>

        <Box sx={{ mb: 2 }}>
          <TextField
            margin="none"
            required
            fullWidth
            label="Email"
            placeholder="Email Address"
            id="email"
            value={values.email}
            name="email"
            type="email"
            onChange={handleChange}
            onBlur={handleBlur}
            autoComplete="email"
            error={Boolean(touched.email && errors.email)}
          />
          {touched.email && errors.email && (
            <FormHelperText error sx={{ mt: 0.5, mx: 1 }}>
              {errors.email}
            </FormHelperText>
          )}
        </Box>

        <Box sx={{ mb: 2 }}>
          <TextField
            margin="none"
            required
            fullWidth
            placeholder="Password"
            id="password"
            name="password"
            label="Password"
            type={showPassword ? "text" : "password"}
            value={values.password}
            onChange={handleChange}
            onBlur={handleBlur}
            autoComplete="new-password"
            error={Boolean(touched.password && errors.password)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword(!showPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {touched.password && errors.password && (
            <FormHelperText error sx={{ mt: 0.5, mx: 1 }}>
              {errors.password}
            </FormHelperText>
          )}
        </Box>

        <LoadingButton
          type="submit"
          loading={isSubmitting}
          loadingIndicator="Registering..."
          fullWidth
          variant="contained"
          size="large"
          sx={{
            mt: 1.5,
            mb: 2,
            py: 1.25,
            fontWeight: 600,
            fontSize: "0.95rem",
            borderRadius: 1.5,
          }}
        >
          Register
        </LoadingButton>
      </Box>
    </FormikProvider>
  );
}
